import { Box, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import SearchContainer from "components/SearchContainer";
import Table from "components/Table";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ResType } from "types";
import { getAutFinancialYears } from "utils/getFinancialYears";
import { clientCategories, tanTracesTypes, tracesFormTypes } from "./constants";

import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import useTitle from "hooks/useTitle";
import { snack } from "components/toast";
import {
  exportClientTracesInbox,
  getAllTraceCommunications,
} from "api/services/automation/income-tax-tan";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { handleError } from "utils/handleError";
import { getCurrentFormattedDateTime } from "utils";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { SortDirection } from "data/constants";
import sortHandler from "components/Table/SortHandler";
import _ from "lodash";

const TraceCommunication = () => {
  useTitle("ATOM Pro | Traces Inbox");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [interval, setInterval] = useState<any>("");

  const [filters, setFilters] = useState<any>({
    formCode: null,
    filingType: null,
    financialYear: null,
    clientCategory: null,
    financialQuarter: null,
    interval: null,
  });
  const forms = tracesFormTypes;
  const financialYear = getAutFinancialYears();
  const quarters = ["Q1", "Q2", "Q3", "Q4"];

  const location = useLocation();
  const state: any = location.state;

  useEffect(() => {
    if (state) {
      setFilters({
        ...filters,
        filingType: state?.filingType,
        financialYear: state?.financialYear,
        financialQuarter: state?.financialQuarter,
        formCode: state?.formCd,
        interval: state?.interval,
      });
      setPage(0);
    }
  }, [state]);

  const intervalData = {
    last1week: "last 1 week",
    last15days: "last 15 days",
    last1month: "last 1 month",
  };

  let defaultColumns = [
    {
      key: "displayName",
      title: (
        <Box display="flex" alignItems="center">
          <PersonOutlineOutlinedIcon style={{ width: "20px", marginRight: "5px" }} />
          Client Name (TAN)
        </Box>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,

      render: (row) => (
        <Typography>
          {row?.tanClientCredentials?.client?.displayName} (
          <span>{row?.tanClientCredentials?.tanNumber}</span>)
        </Typography>
      ),
    },
    {
      key: "date",
      title: "Date",
      width: "120px",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "commRefNo",
      title: "Reference ID",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "fy",
      title: "FY",
      width: "100px",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return <Box>{row?.fy ? row?.fy : "--"}</Box>;
      },
    },
    {
      key: "qt",
      title: "Quarter",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "formType",
      title: "Form Type",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return <Typography>{row?.formType ? row?.formType : "-"}</Typography>;
      },
    },

    {
      key: "commCat",
      title: "Category",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "description",
      title: "Description",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "type",
      title: "Action",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography
            color={row?.type === "Required" ? "#800080" : "#008080"}
            sx={{ fontWeight: "bold" }}
          >
            {row?.type ? row?.type : "-"}
          </Typography>
        );
      },
    },
  ];
  const [sort, setSort] = useState<any>({});
  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));
  const { data, isLoading }: ResType = useQuery(
    [
      "get-traces-communications",
      {
        search: search,
        limit: pageCount,
        offset: page * pageCount,
        ...filters,
        sort,
      },
    ],
    getAllTraceCommunications,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },
    }
  );
  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns: setColumns,
    });
  };

  const handleformDescChange = (e) => {
    setFilters({ ...filters, formCode: e.target.value });
    setPage(0);
  };

  const handleQuarterChange = (e) => {
    setFilters({ ...filters, financialQuarter: e.target.value });
    setPage(0);
  };

  const handleAssessmentYearChange = (e) => {
    setFilters({ ...filters, financialYear: e.target.value });
    setPage(0);
  };

  const onClientCategoryChange = (e) => {
    setFilters({ ...filters, clientCategory: e.target.value });
    setPage(0);
  };

  const handleSearch = (e) => {
    setSearch(e);
    setPage(0);
  };

  const handleIntervalChange = (e: any) => {
    setFilters({ ...filters, interval: e.target.value });
    setPage(0);
  };

  const handleFilingTypeChange = (e) => {
    setFilters({ ...filters, filingType: e.target.value });
    setPage(0);
  };
  const { mutate: mutate1 } = useMutation(exportClientTracesInbox, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      const formattedDateTime = getCurrentFormattedDateTime();
      link.href = file;
      link.download = `ATOM Pro - Income Tax (TAN) Traces Inbox ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Income Tax (TAN) Traces Inbox exported successfully");
    },
    onError: (err: any) => {
      snack.error(err);
      snack.error(handleError(err));
    },
  });
  const handleExport = () => {
    mutate1({
      ...state,
      search: search,
      ...filters,
    });
  };
  return (
    <Box m={1}>
      <Box pr={2} pl={2} sx={{ display: "flex" }}>
        <Box display="flex">
          <Box ml={0.5}>
            <TextField
              label="Financial Year"
              value={filters.financialYear || ""}
              sx={{ width: 120 }}
              onChange={handleAssessmentYearChange}
              size="small"
              select
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {financialYear?.map((year, index) => (
                <MenuItem key={index} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box>
            <Box ml={0.5}>
              <TextField
                name="clientCategory"
                value={filters.clientCategory || ""}
                onChange={onClientCategoryChange}
                size="small"
                label="Category"
                select
                sx={{ width: "100px" }}
              >
                <MenuItem key={""} value={""}>
                  Select
                </MenuItem>
                {clientCategories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          <Box ml={0.5}>
            <TextField
              label="Form Type"
              value={filters.formCode || ""}
              sx={{ width: 120 }}
              onChange={handleformDescChange}
              size="small"
              select
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {forms.map((key, index) => (
                <MenuItem key={index} value={key}>
                  {key}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box ml={0.5}>
            <TextField
              label="Quarter"
              value={filters.financialQuarter || ""}
              sx={{ width: 100 }}
              onChange={handleQuarterChange}
              size="small"
              select
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {quarters.map((key, index) => (
                <MenuItem key={index} value={key}>
                  {key}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box ml={0.5} mr={1}>
            <TextField
              label="Action Type"
              value={filters.filingType || ""}
              sx={{ width: 110 }}
              onChange={handleFilingTypeChange}
              size="small"
              select
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {tanTracesTypes.map((key, index) => (
                <MenuItem key={index} value={key}>
                  {key}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <SearchContainer
          minWidth="250px"
          value={search}
          debounced
          placeHolder="Search by  Name | TAN"
          onChange={handleSearch}
        />
        <Box ml={1}>
          <TextField
            label="Interval"
            value={filters.interval || ""}
            onChange={handleIntervalChange}
            size="small"
            select
            sx={{ width: 150 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            {Object.keys(intervalData).map((key, index) => (
              <MenuItem key={index} value={key}>
                {intervalData[key]}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
          <Tooltip title="Clear Filters">
            <IconButton
              onClick={() => {
                setFilters({});
                setSearch("");
              }}
              color="secondary"
              sx={{
                border: "1px solid",
                borderColor: "secondary.main",
              }}
            >
              <FilterAltOffIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton
              onClick={handleExport}
              color="secondary"
              sx={{
                border: "1px solid",
                borderColor: "secondary.main",
              }}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box mt={1}>
        <Table
          pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
          data={data?.data.result || []}
          columns={columns}
          sortHandler={handleSort}
          loading={isLoading}
          // onRowClick={handleRowClick}
        />
      </Box>
    </Box>
  );
};

export default TraceCommunication;
